// 세리사무소(税理士事務所) 전용 홈 — 포털 본문.
//
// 이름이 zeirishi 인 이유: 税理士事務所(세무 대리 업무를 하는 사무소)이지 税務署(과세 관청)가
// 아니다. taxoffice 로 두면 영어권 개발자가 후자로 읽을 여지가 있어, 직업 자격을 가리키는
// 말을 그대로 쓴다.
//
// 화면 구성은 시안(세무대리인포털_메인_v8.html)을 따른다 — 브리핑 3칸 → 오늘의 업무 →
// 수익 기회. 상단바·우측 레일·토스트는 껍데기(Layout.zeirishi)가 그리고, 이 파일은 본문만
// 그린다. 상태는 컨텍스트(Home.zeirishi.context)에 있다: 브리핑 칸이 여는 업무를 사이드바의
// 업무 이력도 봐야 하고, 검색바의 질문이 사이드바의 AI 대화로 들어가야 하기 때문이다.
//
// 데이터는 Home.zeirishi.data.ts 의 목업이다. 실제로 동작하는 것은 AI 대화 하나뿐이고,
// 그것만 게이트웨이(chat-api·embed-api)를 호출한다 — 연출이 아니다.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ZeirishiWork from './Home.zeirishi.work';
import ZeirishiRevenue from './Home.zeirishi.revenue';
import ZeirishiScheduleWidget from './Home.zeirishi.schedule';
import PaneBoundary from '../../components/PaneBoundary';
import { useZeirishi } from './Home.zeirishi.context';
import { AI_HISTORY, AUTO_ITEMS, AUTO_TOTAL, OPPORTUNITIES, WORK_ITEMS, companyOf, won } from './Home.zeirishi.data';
import styles from './Home.zeirishi.main.module.css';

/** 브리핑 타일의 목적지 — 칸마다 갈 곳이 다르다 */
type BriefGo = 'auto' | 'todo' | 'due' | 'rev';

export default function HomeZeirishi() {
  const { queue, toast, askAi, view, setCurrentCo } = useZeirishi();

  const rootRef = useRef<HTMLDivElement>(null);
  const workRef = useRef<HTMLDivElement>(null);
  const revRef = useRef<HTMLDivElement>(null);

  const [draft, setDraft] = useState('');
  /* AI 와 했던 일 — 검색바의 시계 버튼에서 펼친다 */
  const [histOpen, setHistOpen] = useState(false);
  const histRef = useRef<HTMLDivElement>(null);

  /* behavior 를 넘기지 않는다 — 스크롤 컨테이너에 scroll-behavior:smooth 가 걸려 있고,
     여기서 'smooth' 를 함께 지정하면 scroll-snap 이 애니메이션 도중 원위치로 되돌린다. */
  const scrollTo = useCallback((ref: React.RefObject<HTMLDivElement>) => {
    const el = ref.current;
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'start' });
    }
  }, []);

  const briefGo = useCallback((kind: BriefGo) => {
    if (kind === 'rev') {
      scrollTo(revRef);
      return;
    }
    /* 'AI가 끝냈어요' 칸은 자동 처리 내역으로 가야 한다.
       시안(v10)은 briefGo('auto','a1') 로 부르지만 briefGo 가 인자를 하나만 받아
       'todo' 와 같은 갈래로 떨어졌다 — 눌러도 확인 필요 탭이 열린다. 여기서 바로잡는다. */
    const target = kind === 'auto' ? AUTO_ITEMS[0]
      : kind === 'due' ? WORK_ITEMS[0]
        : queue.liveItems[0];
    if (target) {
      queue.focus(target.id);
      toast(`${target.title} — 내용을 열었습니다`);
    }
    scrollTo(workRef);
  }, [queue, scrollTo, toast]);

  /* ── 키보드 ──
     Enter 로 승인이 이어지는 것이 이 화면의 핵심 동작이다(기획서 6.4).
     다만 모듈 화면으로 이동해도 이 홈은 마운트된 채 숨어 있을 뿐이라, 보이지 않을 때
     키를 먹으면 안 된다 — display:none 이면 offsetParent 가 null 인 것으로 판별한다. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root || root.offsetParent === null) {
        return;
      }
      // target 이 document 일 수 있다 — tagName 을 바로 읽으면 예외가 나 핸들러가 통째로 죽는다
      const el = e.target as HTMLElement | null;
      const tag = el && el.tagName ? el.tagName.toUpperCase() : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.metaKey || e.ctrlKey) {
        return;
      }
      if (e.key === 'Enter' && queue.selectedId && queue.doneIds.indexOf(queue.selectedId) < 0) {
        e.preventDefault();
        queue.approve();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [queue]);

  /* 바깥을 누르거나 Esc 를 누르면 닫는다 — 드롭다운이 본문을 가리고 있기 때문이다 */
  useEffect(() => {
    if (!histOpen) {
      return undefined;
    }
    const onDown = (e: MouseEvent) => {
      const box = histRef.current;
      if (box && !box.contains(e.target as Node)) {
        setHistOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setHistOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [histOpen]);

  const ask = () => {
    askAi(draft);
    setDraft('');
  };

  /* 이력을 누르면 그 질문을 '실제로 다시' 보낸다.
     과거 답변을 대화창에 끼워 넣지 않는 이유: 이 패널은 게이트웨이와 주고받는 진짜 대화라,
     서버가 준 적 없는 답이 섞이면 사용자가 그것을 응답으로 읽는다. 회사 컨텍스트만
     그때 기준으로 맞춰 주고, 답은 지금 다시 받는다. */
  const resumeAi = (idx: number) => {
    const h = AI_HISTORY[idx];
    setHistOpen(false);
    if (h.co) {
      setCurrentCo(h.co);
    }
    askAi(h.q);
    toast(`${h.tm} 질문을 다시 보냈습니다 — 답은 새로 받습니다`);
  };

  /* 업무가 먼저인가 — 기획서 5.1 의 v8(업무 우선)/v9(수익 우선)이 이 한 줄로 합쳐진다 */
  const workFirst = view === 'work';

  const remain = queue.liveItems.length;
  /* 타일에 쓰는 값 — 따로 저장해 둔 것이 아니라 지금 큐에서 계산한다.
     승인할 때마다 브리핑도 같이 줄어야 아침에 읽은 숫자가 거짓말이 되지 않는다. */
  const doneTotal = AUTO_TOTAL + queue.doneIds.length;
  const top = queue.liveItems[0];
  const oppSum = OPPORTUNITIES.reduce((a, b) => a + (b.fee || 0), 0);
  /* 건당 40초 — 남은 일이 '몇 분짜리인지' 가 남은 건수보다 마음에 와닿는다 */
  const mins = Math.max(1, Math.round(remain * 40 / 60));

  const tiles: {
    key: string; tone: string; dot: string; label: string;
    value: string; unit: string; desc: string; go: () => void;
  }[] = [
    {
      key: 'ok', tone: styles.tileOk, dot: '#34c759', label: 'AI가 끝냈어요',
      value: String(doneTotal), unit: '건', desc: '오류 0건 · 손대실 것 없습니다',
      go: () => briefGo('auto'),
    },
    {
      key: 'wr', tone: styles.tileWr, dot: '#f08300', label: '확인 부탁드려요',
      value: String(remain), unit: '건',
      desc: remain ? `건당 약 40초 · 다 해도 ${mins}분` : '지금은 없습니다',
      go: () => briefGo('todo'),
    },
    {
      key: 'rd', tone: styles.tileRd, dot: '#ff3b30', label: '가장 급한 일',
      value: top ? top.due : '없음', unit: '',
      desc: top ? top.title : '기한 임박 건이 없습니다',
      go: () => briefGo('todo'),
    },
    {
      key: 'pp', tone: styles.tilePp, dot: '#af52de', label: '새 수익 기회',
      value: won(Math.round(oppSum / 10000)), unit: '만원',
      desc: `제안 ${OPPORTUNITIES.length}건 · 제안서까지 준비됐어요`,
      go: () => briefGo('rev'),
    },
  ];

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.scroll}>

        {/* ── 첫 화면 — 브리핑과 오늘의 업무까지 ── */}
        <div className={styles.screen1}>
          <div className={styles.center}>
            <h1 className={styles.pname}>세무법인 정담 <span className={styles.accent}>Works</span></h1>
          </div>

          {/* 묻기(질문)와 실행(지시)을 함께 받는 자리 — 사이드바의 AI 대화로 들어간다 */}
          <div className={styles.searchWrap} ref={histRef}>
            <div className={styles.search}>
              <svg className={styles.sIco} viewBox="0 0 24 24" fill="none" stroke="#7a8fa8" strokeWidth="2.2" strokeLinecap="round">
                <circle cx="11" cy="11" r="7" /><path d="m20.5 20.5-3.6-3.6" />
              </svg>
              <input
                value={draft}
                placeholder="궁금한 점을 물어보거나 원하는 작업을 시작하세요."
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    ask();
                  }
                }}
              />
              <button
                type="button"
                className={`${styles.shist} ${histOpen ? styles.shistOn : ''}`}
                title="AI 작업 이력 — 했던 질문을 다시 보냅니다"
                onClick={() => setHistOpen(o => !o)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1M3.5 4v5h5M12 8v4.5l3 1.8" />
                </svg>
              </button>
              <button type="button" className={styles.oneai} onClick={ask}>✦ AI</button>
            </div>

            {/* ── AI 와 했던 일 ── */}
            {histOpen && (
              <div className={styles.ahist}>
                <div className={styles.ahh}>
                  <span className={styles.tl2}>
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="#fff">
                      <path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17l-1.9-5.1L4.5 10l5.6-1.4z" />
                    </svg>
                  </span>
                  <span className={styles.ahhT}>AI와 했던 일</span>
                  <span className={styles.ahhS}>최근 {AI_HISTORY.length}건 · 눌러서 다시 묻기</span>
                  <button type="button" className={styles.ahhX} onClick={() => setHistOpen(false)}>✕</button>
                </div>
                {AI_HISTORY.map((h, i) => (
                  <div key={h.tm + h.q} className={styles.ahr} onClick={() => resumeAi(i)}>
                    <span className={styles.at2}>{h.tm}</span>
                    <span className={styles.ab2}>
                      <span className={styles.aq}>{h.q}</span>
                      <span className={styles.aa}>{h.a}{h.note ? ` · ${h.note}` : ''}</span>
                    </span>
                    <span className={styles.ac}>{h.co ? companyOf(h.co).ab : '전체'}</span>
                    {/* 배지는 그때 일이 끝났는지를 말한다 — 누르면 어느 쪽이든 질문을 다시 보낸다 */}
                    <span className={`${styles.ast} ${h.done ? styles.astDone : styles.astGo}`}>
                      {h.done ? '완료' : '남은 일 있음'}
                    </span>
                  </div>
                ))}
                <div className={styles.ahf} onClick={() => { setHistOpen(false); askAi(''); }}>
                  전체 대화 기록 보기 →
                </div>
              </div>
            )}
          </div>

          {/* ── 아침 브리핑 — 인사 한마디와 숫자 네 칸 ── */}
          <div className={styles.brief3}>
            <div className={styles.bcard}>
              <div className={styles.bhead}>
                <span className={styles.borb}>
                  <svg viewBox="0 0 24 24" width="21" height="21" fill="#fff">
                    <path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17l-1.9-5.1L4.5 10l5.6-1.4z" />
                  </svg>
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className={styles.bhi}>
                    {remain ? '김세무 세무사님, 좋은 아침입니다.' : '김세무 세무사님, 오늘 몫은 다 끝났습니다.'}
                  </div>
                  <div className={styles.bsub}>
                    {remain ? (
                      <>밤사이 <b>{doneTotal}건</b>을 정리해 두었어요. <b>{remain}건</b>만 봐 주시면 오늘 할 일은 끝납니다.</>
                    ) : (
                      <>확인이 필요한 일까지 <b>모두</b> 마쳤습니다. 남은 건 새 수익 기회 <b>{won(Math.round(oppSum / 10000))}만원</b>뿐이에요.</>
                    )}
                  </div>
                </div>
                <span className={styles.bstamp}>
                  오전 8시 브리핑<br /><span className={styles.bst2}>2026. 9. 16 (수)</span>
                </span>
              </div>

              <div className={styles.btiles}>
                {tiles.map(t => (
                  <button key={t.key} type="button" className={`${styles.btile} ${t.tone}`} onClick={t.go}>
                    <span className={styles.bk}><i style={{ background: t.dot }} />{t.label}</span>
                    <span className={styles.bv}>{t.value}{t.unit ? <small>{t.unit}</small> : null}</span>
                    <span className={styles.bd}>{t.desc}</span>
                  </button>
                ))}
              </div>

              <div className={styles.bfoot}>
                {remain ? (
                  <><span className={styles.sm2}>▼ 어제 이 시간보다 3건 적어요</span> · 이 속도면 오전 중에 마무리됩니다</>
                ) : (
                  <><span className={styles.sm2}>✓ 오늘 확인할 일 없음</span> · 수고하셨어요. 좋은 하루 되세요</>
                )}
              </div>
            </div>
          </div>

          {/* 내 일정 위젯 — 오늘부터 7일(본인 범위). 위젯이 죽어도 홈은 살아 있게 오류 경계로 감싼다 */}
          <PaneBoundary name="zeirishi-schedule">
            <ZeirishiScheduleWidget />
          </PaneBoundary>

          {/* 보기 설정에 따라 첫 화면에 오는 덩어리가 바뀐다. 둘 다 늘 화면에 있고 자리만
              맞바꾼다 — 자리를 옮기면 그 자리의 컴포넌트는 다시 마운트되지만, 고르던 값
              (업무 큐·수익 갈래)은 컨텍스트에 있어 풀리지 않는다. */}
          <div ref={workFirst ? workRef : revRef}>
            {workFirst
              ? <ZeirishiWork queue={queue} toast={toast} onGoRevenue={() => scrollTo(revRef)} />
              : <ZeirishiRevenue queue={queue} onOpenInPanel={() => scrollTo(workRef)} />}
          </div>

          {/* 아래에 무엇이 접혀 있는지 알린다 */}
          <div className={styles.scrollhint}>
            <button type="button" className={styles.shBtn} onClick={() => scrollTo(workFirst ? revRef : workRef)}>
              {workFirst ? '수익 기회와 이번 달 성과 보기' : '오늘의 업무 보기'}<i>⌄</i>
            </button>
          </div>
        </div>

        {/* ── 둘째 화면 ── */}
        <div className={styles.screen2} ref={workFirst ? revRef : workRef}>
          {workFirst
            ? <ZeirishiRevenue queue={queue} onOpenInPanel={() => scrollTo(workRef)} />
            : <ZeirishiWork queue={queue} toast={toast} onGoRevenue={() => scrollTo(revRef)} />}
        </div>

      </div>
    </div>
  );
}
